import { atom } from 'jotai'
import { unwrap } from 'jotai/utils'
import { atomFamily } from 'jotai-family'
import { relayMembershipsChannelAtom } from './invalidation'
import { favoriteRelaysWithSetsAtom } from './lists'
import { loadRelayInfo } from '@nostr/gadgets/relays'
import { pool } from '@nostr/gadgets/global'
import { appendUnique, isHex32 } from '@nostr/gadgets/utils'

export type RelayRole = {
  id: string
  label?: string
  description?: string
  color?: string
  order?: number
}

const ROLES_KIND = 33534
const MEMBERSHIPS_KIND = 13534

export const visitedRelaysAtom = atom<string[]>([])

export const knownRelaysAtom = atom(async (get) => {
  const feeds = window.hallway.universe.hardcodedFeeds
  const visited = get(visitedRelaysAtom)
  const favs = await get(favoriteRelaysWithSetsAtom)

  const urls: string[] = [...visited]
  for (const item of favs) {
    if (typeof item === 'string') appendUnique(urls, item)
    else for (const url of item.items) appendUnique(urls, url)
  }
  for (const feed of feeds) if (feed.relays) appendUnique(urls, ...feed.relays)

  return urls
})

export const relayMembersAtomFamily = atomFamily((url: string) =>
  atom(async () => {
    const info = await loadRelayInfo(url)
    if (!info || !info.self || !info.supported_nips?.includes(43)) return []

    const events = await pool.querySync([url], {
      kinds: [ROLES_KIND, MEMBERSHIPS_KIND],
      authors: [info.self]
    })

    const idx = events.findIndex((event) => event.kind === MEMBERSHIPS_KIND)
    if (idx === -1) return []
    const membershipEvent = events[idx]
    events[idx] = events[events.length - 1]
    events.length = events.length - 1

    const allRoles = new Map<string, RelayRole>()
    for (const event of events) {
      const role: RelayRole = { id: '' }
      for (const [name, value] of event.tags) {
        switch (name) {
          case 'd':
            role.id = value
            break
          case 'label':
            role.label = value
            break
          case 'description':
            role.description = value
            break
          case 'color':
            role.color = value
            break
          case 'order': {
            const n = Number(value)
            if (Number.isFinite(n)) role.order = n
            break
          }
        }
      }
      if (!role.id) continue
      allRoles.set(role.id, role)
    }

    const members = new Map<string, RelayRole[]>()
    for (const [tagName, pubkey, ...roles] of membershipEvent.tags) {
      if (tagName !== 'member') continue
      if (!isHex32(pubkey)) continue
      members.set(
        pubkey,
        roles.map((id) => allRoles.get(id)).filter((v): v is RelayRole => !!v)
      )
    }

    return members
  })
)

export const membershipsByPubkeyAtom = atom(async (get) => {
  get(relayMembershipsChannelAtom)

  const visited = await get(knownRelaysAtom)
  const result = new Map<string, Map<string, RelayRole[]>>()

  await Promise.allSettled(
    [...visited].map(async (url) => {
      const members = await get(relayMembersAtomFamily(url))
      for (const [pubkey, roles] of members) {
        let inner = result.get(pubkey)
        if (!inner) {
          inner = new Map()
          result.set(pubkey, inner)
        }
        inner.set(url, roles)
      }
    })
  )

  return result
})

export const unwrappedKnownRelaysAtom = unwrap(knownRelaysAtom)
export const unwrappedMembershipsByPubkeyAtom = unwrap(membershipsByPubkeyAtom)
